import React, { useEffect, useCallback, useRef, useState, useMemo } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Lightbox — minimalist fullscreen image viewer.
 * Desktop: click prev/next buttons or arrow keys, Escape to close.
 * Mobile: horizontal swipe to navigate, tap close button to close.
 * Vertical gestures pass through so the page can still scroll while closing.
 */
const SWIPE_THRESHOLD = 60; // px horizontal delta to trigger navigation
const DIRECTION_TOLERANCE = 1.3; // horizontal must exceed vertical * this

const Lightbox = ({ open, onClose, item, items = [], onNavigate }) => {
  const hasNav = items && items.length > 1 && typeof onNavigate === "function";
  const currentIndex = useMemo(() => {
    if (!item || !items || items.length === 0) return -1;
    return items.findIndex((p) => p.id === item.id);
  }, [item, items]);

  // Swipe animation offset (px). Positive = image dragged to right (prev), negative = left (next)
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const touchStart = useRef({ x: 0, y: 0 });
  const isHorizontal = useRef(false);
  const gestureDecided = useRef(false);

  const goTo = useCallback(
    (nextIndex) => {
      if (!hasNav) return;
      const next = items[(nextIndex + items.length) % items.length];
      if (next) onNavigate(next);
    },
    [hasNav, items, onNavigate]
  );

  const goNext = useCallback(() => {
    if (currentIndex >= 0) goTo(currentIndex + 1);
  }, [currentIndex, goTo]);

  const goPrev = useCallback(() => {
    if (currentIndex >= 0) goTo(currentIndex - 1);
  }, [currentIndex, goTo]);

  // Keyboard + body scroll lock
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose, goNext, goPrev]);

  // Reset drag when active item changes (e.g. after swipe completes)
  useEffect(() => {
    setDragX(0);
    setDragging(false);
  }, [item?.id]);

  // Preload neighbours for a snappy swipe experience
  useEffect(() => {
    if (!open || !hasNav || currentIndex < 0) return;
    const preloadList = [
      items[(currentIndex + 1) % items.length],
      items[(currentIndex - 1 + items.length) % items.length],
    ];
    preloadList.forEach((it) => {
      if (it?.src) {
        const img = new Image();
        img.src = it.src;
      }
    });
  }, [open, hasNav, currentIndex, items]);

  // Touch handlers — mobile swipe
  const onTouchStart = (e) => {
    if (!hasNav) return;
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
    isHorizontal.current = false;
    gestureDecided.current = false;
    setDragging(true);
  };

  const onTouchMove = (e) => {
    if (!dragging) return;
    const t = e.touches[0];
    const dx = t.clientX - touchStart.current.x;
    const dy = t.clientY - touchStart.current.y;

    // Decide gesture direction on the first meaningful movement
    if (!gestureDecided.current && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
      isHorizontal.current = Math.abs(dx) > Math.abs(dy) * DIRECTION_TOLERANCE;
      gestureDecided.current = true;
      if (!isHorizontal.current) {
        // Vertical gesture → cancel drag so page can behave naturally
        setDragging(false);
        setDragX(0);
        return;
      }
    }
    if (isHorizontal.current) {
      // Prevent scroll from being confused with swipe
      if (e.cancelable) e.preventDefault();
      setDragX(dx);
    }
  };

  const onTouchEnd = () => {
    if (!dragging) return;
    if (isHorizontal.current && Math.abs(dragX) > SWIPE_THRESHOLD) {
      if (dragX < 0) goNext();
      else goPrev();
    } else {
      setDragX(0);
    }
    setDragging(false);
  };

  if (!open || !item) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-12 fade-up"
      onClick={onClose}
      data-testid="lightbox-backdrop"
      role="dialog"
      aria-modal="true"
    >
      {/* Close */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-5 right-5 md:top-8 md:right-8 w-11 h-11 border border-white/25 text-white flex items-center justify-center hover:bg-[#E11D2E] hover:border-[#E11D2E] transition-colors z-10"
        aria-label="Sluiten"
        data-testid="lightbox-close"
      >
        <X size={18} />
      </button>

      {/* Prev — desktop only */}
      {hasNav && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            goPrev();
          }}
          className="hidden md:flex absolute left-5 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 border border-white/25 text-white items-center justify-center hover:bg-[#E11D2E] hover:border-[#E11D2E] transition-colors z-10"
          aria-label="Vorige"
          data-testid="lightbox-prev"
        >
          <ChevronLeft size={20} />
        </button>
      )}

      {/* Next — desktop only */}
      {hasNav && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            goNext();
          }}
          className="hidden md:flex absolute right-5 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 border border-white/25 text-white items-center justify-center hover:bg-[#E11D2E] hover:border-[#E11D2E] transition-colors z-10"
          aria-label="Volgende"
          data-testid="lightbox-next"
        >
          <ChevronRight size={20} />
        </button>
      )}

      {/* Content — swipeable */}
      <figure
        className="relative max-w-[1400px] w-full max-h-full flex flex-col items-center select-none"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        style={{ touchAction: hasNav ? "pan-y" : "auto" }}
      >
        <img
          src={item.src}
          alt={item.alt || ""}
          className="block max-w-full max-h-[80vh] object-contain shadow-[0_30px_60px_-30px_rgba(0,0,0,0.6)]"
          data-testid="lightbox-image"
          draggable={false}
          style={{
            transform: `translateX(${dragX}px)`,
            transition: dragging ? "none" : "transform 350ms cubic-bezier(0.16, 1, 0.3, 1)",
            opacity: dragging ? Math.max(0.55, 1 - Math.abs(dragX) / 600) : 1,
            willChange: "transform, opacity",
          }}
        />
        {(item.title || item.category) && (
          <figcaption className="mt-6 text-center text-white">
            {item.category && (
              <p className="text-[10px] uppercase tracking-[0.28em] text-white/60 mb-2">
                {item.category}
              </p>
            )}
            {item.title && (
              <p className="font-display text-xl md:text-2xl font-light tracking-tight">
                {item.title}
              </p>
            )}
            {hasNav && (
              <p
                className="mt-3 text-[10px] uppercase tracking-[0.28em] text-white/40 tabular-nums"
                data-testid="lightbox-counter"
              >
                {String(currentIndex + 1).padStart(2, "0")}
                <span className="mx-2">/</span>
                {String(items.length).padStart(2, "0")}
              </p>
            )}
          </figcaption>
        )}
      </figure>
    </div>
  );
};

export default Lightbox;
